import { useEffect, useState } from 'react';

import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { getMeta, type DesafioValores } from '../../campanhas/desafioPessoalCatalog';
import { diasDoPeriodo, ritmoDoPeriodo, type Janela } from './periodo';
import type { ObjetivoDoMes } from './useObjetivoDoMes';

/**
 * O alvo de um SEMESTRE ou de um ANO: a soma das metas mensais do período.
 *
 * Não existe "meta do semestre" cadastrada em lugar nenhum — o cadastro é
 * mensal, no mês, e é assim que continua (mexer nele não fazia parte do
 * pedido). Somar os meses da janela é a leitura fiel do que foi combinado: se
 * o gerente tem R$ 2 mi em cada mês de jul a dez, a meta do 2º semestre é R$
 * 12 mi, e "faltam" é essa soma menos o vendido no período.
 *
 * ── POR QUE ESTE HOOK NÃO RESPONDE PELO MÊS ─────────────────────────────────
 * Com `periodo === 'mes'` ele devolve NULL de propósito: o mês já tem dono, o
 * useObjetivoDoMes, que lê a view `desafio_pessoal_progresso` (com o realizado
 * calculado no banco) e é quem alimenta os outros cards do Início. Responder
 * pelo mês aqui também criaria duas contas do mesmo número na mesma tela — e
 * elas divergiriam, porque o realizado do bento exclui as contempladas e o da
 * view não.
 *
 * O realizado daqui vem POR FORA (`realizadoPeriodo`): é a produção que o card
 * está exibindo. Recalcular seria a segunda fonte do mesmo número.
 */

type Entrada = {
  scopeLoading: boolean;
  isSuperAdmin: boolean;
  isManager: boolean;
  currentCrmUserId: number | null;
  viewAsCrmUserId: number | null;
  janela: Janela;
  /** Produção do escopo visível DENTRO da janela. */
  realizadoPeriodo: number;
};

export const useAlvoDoPeriodo = ({
  scopeLoading,
  isSuperAdmin,
  isManager,
  currentCrmUserId,
  viewAsCrmUserId,
  janela,
  realizadoPeriodo,
}: Entrada) => {
  const [alvoDoPeriodo, setAlvoDoPeriodo] = useState<ObjetivoDoMes | null>(null);
  // Conteúdo, não referência: a janela é remontada a cada troca de período.
  const mesesDaJanela = janela.meses.join(',');

  useEffect(() => {
    if (scopeLoading || janela.periodo === 'mes') {
      setAlvoDoPeriodo(null);
      return;
    }
    let ativo = true;
    const meses = janela.meses.map((chave) => Number(chave.slice(5, 7)));
    const dias = diasDoPeriodo(janela);

    const publicar = (vocabulario: 'objetivo' | 'meta', alvo: number) => {
      if (!ativo) return;
      // Alvo zero não é alvo: é período sem meta cadastrada, e o card diz isso
      // em vez de fingir uma corrida contra nada.
      if (alvo <= 0) {
        setAlvoDoPeriodo(null);
        return;
      }
      setAlvoDoPeriodo({
        vocabulario,
        alvo,
        realizado: realizadoPeriodo,
        pct: Math.min(100, Math.round((realizadoPeriodo * 100) / alvo)),
        falta: Math.max(0, alvo - realizadoPeriodo),
        diasRestantes: dias.totais - dias.decorridos,
        ritmoPct: ritmoDoPeriodo(realizadoPeriodo, alvo, dias),
      });
    };

    (async () => {
      // Especialista — e o super admin "vendo como" um: os objetivos pessoais
      // dos meses da janela. A view do mês não serve aqui (ela só existe para o
      // mês corrente, por decisão da migration 107), então lê-se a tabela.
      const pessoal = viewAsCrmUserId ?? (!isSuperAdmin && !isManager ? currentCrmUserId : null);
      if (pessoal != null) {
        const { data } = await supabase
          .from('desafio_pessoal')
          .select('respostas')
          .eq('crm_user_id', pessoal)
          .eq('ano', janela.ano)
          .in('mes', meses);
        const alvo = ((data ?? []) as { respostas: DesafioValores }[]).reduce(
          (soma, linha) => soma + (getMeta(linha.respostas) ?? 0),
          0,
        );
        publicar('objetivo', alvo);
        return;
      }

      // Gestão: as metas North dos meses da janela (a RLS recorta — gerente vê
      // a própria, super admin vê todas), somadas.
      const { data } = await supabase
        .from('meta_north')
        .select('valor')
        .eq('ano', janela.ano)
        .in('mes', meses);
      const alvo = ((data ?? []) as { valor: number | string }[]).reduce(
        (soma, linha) => soma + Number(linha.valor || 0),
        0,
      );
      publicar('meta', alvo);
    })();

    return () => {
      ativo = false;
    };
    // A janela entra por CONTEÚDO (período, ano, meses), não por referência: o
    // HomeDashboard a remonta a cada troca, e depender do objeto faria o hook
    // refazer a consulta a cada render.
  }, [
    scopeLoading,
    isSuperAdmin,
    isManager,
    currentCrmUserId,
    viewAsCrmUserId,
    janela.periodo,
    janela.ano,
    mesesDaJanela,
    realizadoPeriodo,
  ]);

  return alvoDoPeriodo;
};
